import React, { AllHTMLAttributes, FC } from 'react'
import { FontAwesomeSvgIcon as FontAwesomeIcon } from 'react-fontawesome-svg-icon'

import { faExclamationTriangle } from '@fortawesome/pro-light-svg-icons/faExclamationTriangle'
import clsx from 'clsx'

import { InputStyle } from '../Input/Input'
import { Spinner } from '../Spinner/Spinner'
import { Text } from '../Text/Text'

export interface CheckboxProps extends Omit<AllHTMLAttributes<HTMLInputElement>, 'label'> {
  /**
   * The name of the input element
   */
  name: string
  /** Is this checkbox disabled */
  disabled?: boolean
  /** The label to present beside the input */
  label: React.ReactNode | string
  /** An optional helper description to present beside the label */
  note?: React.ReactNode | string
  /** If present, the input will be styled with danger colors. If a React node is supplied, it will be rendered alongside the label */
  error?: React.ReactNode | string
  /** If true a loading spinner will be shown in place of the checkbox */
  loading?: boolean
}

export const CheckboxStyle = {
  input: {
    disabled: 'bg-gray-200 border-gray-200 cursor-not-allowed',
    error: 'border-red-500 ring-red-500'
  },
  label: {
    _: 'font-body text-black cursor-pointer',
    disabled: 'text-gray-400 cursor-not-allowed',
    error: 'text-red-600',
    note: 'text-gray-600'
  }
}

/**
 * Checkbox - A checkbox input with optional label
 */
export const Checkbox: FC<CheckboxProps> = React.forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { className, disabled, error, label, loading, name, note, ...props },
  ref
) {
  const errorContent = (
    <>
      <div className='flex items-center'>
        <FontAwesomeIcon className='mr-2 text-red-500' icon={faExclamationTriangle} />
        <Text as='span' className={clsx(InputStyle.label.error)} theme='sm'>
          {error}
        </Text>
      </div>
    </>
  )

  return (
    <>
      <label className={clsx('relative flex items-center', className)}>
        <div className='flex items-center'>
          {loading ? (
            <Spinner className='mr-2' />
          ) : (
            <input
              ref={ref}
              {...props}
              aria-describedby={`${name}-description`}
              aria-labelledby={`${name}-label`}
              className={clsx(error && CheckboxStyle.input.error)}
              disabled={disabled}
              name={name}
              type='checkbox'
            />
          )}
        </div>
        <div className={clsx(CheckboxStyle.label._, disabled && CheckboxStyle.label.disabled)}>
          <Text as='span' className={disabled ? 'cursor-not-allowed' : ''} id={`${name}-label`}>
            {label}
          </Text>
          {note ? (
            <Text className={CheckboxStyle.label.note} id={`${name}-description`} theme='sm'>
              <span className='sr-only'>{label}</span>
              {note}
            </Text>
          ) : null}
        </div>
      </label>
      <div className='mt-2'>{error ? errorContent : null}</div>
    </>
  )
})
